import * as React from "react";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import "./BrandWindow.scss";

type DockItem = { id: string; title: string; restore: () => void };
type DockContextValue = {
  add: (item: DockItem) => void;
  remove: (id: string) => void;
};

const DockContext = createContext<DockContextValue>({
  add: () => {},
  remove: () => {},
});

export const useMinimizedDock = () => useContext(DockContext);

/**
 * Holds any minimized windows and renders them as collapsed title-bar chips
 * at the bottom-left of the screen. Clicking a chip restores its window.
 */
export const MinimizedDockProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const [items, setItems] = useState<DockItem[]>([]);

  const add = useCallback((item: DockItem) => {
    setItems((prev) =>
      prev.some((p) => p.id === item.id)
        ? prev.map((p) => (p.id === item.id ? item : p))
        : [...prev, item]
    );
  }, []);

  const remove = useCallback((id: string) => {
    // Return the same array reference when there's nothing to remove, so we
    // don't trigger a needless re-render (and, with a stable context value,
    // avoid a render→effect→setState feedback loop).
    setItems((prev) =>
      prev.some((p) => p.id === id) ? prev.filter((p) => p.id !== id) : prev
    );
  }, []);

  // Stable context value — a fresh object here would change `dock`'s identity
  // every render, re-firing the effect in every mounted BrandModal.
  const value = useMemo(() => ({ add, remove }), [add, remove]);

  return (
    <DockContext.Provider value={value}>
      {children}
      {items.length > 0 && (
        <div className="minDock">
          {items.map((it) => (
            <button
              key={it.id}
              type="button"
              className="minDock-item"
              onClick={it.restore}
            >
              {it.title}
            </button>
          ))}
        </div>
      )}
    </DockContext.Provider>
  );
};
